import { useEffect, useRef } from 'react';
import { DeviceEventEmitter } from 'react-native';
import { compareVersions } from 'compare-versions';
import { useAppVersion } from '../hooks/useAppVersion';
import { useIsForegroundNow } from '../hooks/useIsForegroundNow';
import { useLogAnalyticsEvent } from '../hooks/useLogAnalyticsEvent';
import { isAndroid, isNotNil } from '../utils';

const MIN_ANDROID_VERSION = '2.363.0';

// BackHandler 체인(LIFO + 선-consume 중단)은 BottomSheet/Modal 이 선점하면 로그 콜백이 호출되지 않음.
// DeviceEventEmitter 의 hardwareBackPress 는 BackHandler 내부 리스너와 별도 구독자라 consume 무관하게 항상 1회 발화.
// useIsForegroundNow 는 foregroundMap[instanceId] 기반이라 멀티 Activity 환경에서도 top 1개만 true.
export const AndroidBackPressLogger = () => {
  const isForeground = useIsForegroundNow();
  const appVersion = useAppVersion();
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const shouldLog =
    isAndroid &&
    isNotNil(appVersion) &&
    compareVersions(appVersion, MIN_ANDROID_VERSION) >= 0;
  const shouldLogRef = useRef(shouldLog);
  shouldLogRef.current = shouldLog;

  useEffect(() => {
    // 포그라운드(top) 화면만 리스너를 등록한다. 백그라운드로 쌓인 화면들은 등록하지 않아,
    // 뒤로가기 시 N개 리스너가 전부 발화(O(N))하던 걸 top 1개만 발화하도록 줄인다.
    // (isForeground 는 foregroundMap[instanceId] 기반이라 멀티 Activity 에서도 top 1개만 true)
    if (!isAndroid || !isForeground) return;
    const sub = DeviceEventEmitter.addListener('hardwareBackPress', () => {
      if (!shouldLogRef.current) return;
      logAnalyticsEvent({
        name: 'CLICK_BACK_BUTTON',
        analytics: { IS_LAST_ACTIVITY: false },
      });
    });
    return () => sub.remove();
  }, [isForeground, logAnalyticsEvent]);

  return null;
};
